/**
 * dsp-fft-mixed.js — БПФ для длин вида 2^a · {1,3,5,15}.
 *
 * ЗАЧЕМ. Модели MDX-Net требуют СВОЮ длину окна, и она почти никогда не
 * степень двойки: Kim_Vocal_2 — 7680 (2⁹·15), UVR_MDXNET_KARA_2 — 5120 (2¹⁰·5).
 * Радикс-2 такие длины считать не умеет (в 2026-05 это уже стоило инцидента:
 * половина бинов оставалась в неинициализированном состоянии и весь выход
 * уходил в NaN), поэтому там стоял алгоритм Блюстейна: произвольное N сводится
 * к циклической свёртке длиной M = следующая степень двойки ≥ 2N−1. Для 7680
 * это M = 16384, то есть ДВА комплексных БПФ длиной 16384 плюс три прохода по
 * 16384 — вместо одного преобразования длиной 7680.
 *
 * ЗАМЕР 2026-08-22 (профиль V8, Chrome с живым WebGPU, /vocal-remover, Ультра):
 * из 4689 мс на разделение 7-секундного клипа **60.2 % (2821 мс) уходило в
 * `fftInPlace`**, а на саму нейросеть — 0.9 % (41 мс). Узкое место инструмента
 * оказалось не в модели, а в нашем БПФ.
 *
 * КАК. Классическое разложение Кули—Тьюки на два шага, N = N1·N2:
 *   N1 = наибольшая степень двойки в N, N2 = нечётный остаток (1, 3, 5 или 15).
 *   1. N2 преобразований длиной N1 по прореженным с шагом N2 подпоследовательностям
 *      (обычный радикс-2, но с ТАБЛИЦЕЙ поворотов, а не рекуррентой);
 *   2. умножение на W_N^{n2·k1};
 *   3. N1 преобразований длиной N2 — кодлетами DFT-3 / DFT-5 / DFT-15.
 * Вывод формулы — в комментарии над `fft()`.
 *
 * ⚠️ Тонкость, из-за которой такой код обычно и ломают: шаг 1 читает вход с
 * шагом N2, а пишет результат так, что бин k = k1 + N1·k2. Перепутать порядок
 * (k = k2 + N2·k1) — получить правдоподобный, но неверный спектр: ошибка не
 * даёт ни NaN, ни исключения. Поэтому сторож `tests/dsp/fft-mixed.test.mjs`
 * сверяет результат с ПРЯМЫМ ДПФ по определению, а не сам с собой.
 *
 * Длины вне класса 2^a·{1,3,5,15} модуль не берёт (`plan()` вернёт null) —
 * вызывающий обязан иметь запасной путь.
 */
(function (factory) {
    var api = factory();
    if (typeof module === 'object' && module.exports) { module.exports = api; }
    if (typeof window !== 'undefined') { window.DspFftMixed = api; }
    if (typeof self !== 'undefined' && typeof window === 'undefined') { self.DspFftMixed = api; }
})(function () {
    'use strict';

    var SUPPORTED_ODD = { 1: 1, 3: 1, 5: 1, 15: 1 };

    /** Таблица поворотов радикс-2 длины n: W_n^i для i < n/2. */
    function radix2Twiddles(n) {
        var half = n >> 1;
        var cos = new Float64Array(half), sin = new Float64Array(half);
        for (var i = 0; i < half; i++) {
            var a = -2 * Math.PI * i / n;
            cos[i] = Math.cos(a); sin[i] = Math.sin(a);
        }
        return { cos: cos, sin: sin };
    }

    /**
     * План преобразования. null — длина не поддерживается.
     * Таблицы считаются один раз и живут в плане: пересчёт синусов на каждом
     * кадре — вторая по величине статья расхода после самих бабочек.
     */
    function plan(n) {
        if (!(n > 0) || (n | 0) !== n) { return null; }
        var pow2 = 1, odd = n;
        while ((odd & 1) === 0) { odd >>= 1; pow2 <<= 1; }
        if (!SUPPORTED_ODD[odd]) { return null; }

        var p = { n: n, n1: pow2, n2: odd, tw2: radix2Twiddles(pow2) };

        // Поворот шага 2: W_N^{n2 · k1}. Хранится как [n2][k1] — так шаг 2
        // читает память подряд.
        if (odd > 1) {
            var cr = new Float64Array(odd * pow2), ci = new Float64Array(odd * pow2);
            for (var b = 0; b < odd; b++) {
                for (var k1 = 0; k1 < pow2; k1++) {
                    var a = -2 * Math.PI * b * k1 / n;
                    cr[b * pow2 + k1] = Math.cos(a);
                    ci[b * pow2 + k1] = Math.sin(a);
                }
            }
            p.twStep = { cos: cr, sin: ci };
        }
        // Рабочие буферы — по одному на план, а не на вызов. Прежний код
        // выделял два Float64Array(16384) на КАЖДЫЙ кадр: 262 КБ обнуления
        // 1024 раза за чанк.
        p.scratchRe = new Float64Array(n);
        p.scratchIm = new Float64Array(n);
        p.colRe = new Float64Array(pow2);
        p.colIm = new Float64Array(pow2);
        return p;
    }

    /** Радикс-2 БПФ на месте по таблице поворотов. re/im длиной n = 2^k. */
    function fft2(re, im, tw, off, n) {
        off = off || 0;
        for (var i = 1, j = 0; i < n; i++) {
            var bit = n >> 1;
            for (; j & bit; bit >>= 1) { j ^= bit; }
            j ^= bit;
            if (i < j) {
                var t = re[off + i]; re[off + i] = re[off + j]; re[off + j] = t;
                t = im[off + i]; im[off + i] = im[off + j]; im[off + j] = t;
            }
        }
        var cos = tw.cos, sin = tw.sin;
        for (var size = 2; size <= n; size <<= 1) {
            var half = size >> 1;
            var step = n / size;              // шаг по таблице длины n/2
            for (var i2 = 0; i2 < n; i2 += size) {
                for (var j2 = 0, tIdx = 0; j2 < half; j2++, tIdx += step) {
                    var cR = cos[tIdx], cI = sin[tIdx];
                    var a = off + i2 + j2, bIdx = a + half;
                    var uR = re[a], uI = im[a];
                    var xr = re[bIdx], xi = im[bIdx];
                    var vR = xr * cR - xi * cI;
                    var vI = xr * cI + xi * cR;
                    re[a] = uR + vR; im[a] = uI + vI;
                    re[bIdx] = uR - vR; im[bIdx] = uI - vI;
                }
            }
        }
    }

    // ── Кодлеты малых ДПФ ────────────────────────────────────────────────
    // W = e^{-2πi/3}; b·W + c·W² = −½·(b+c) − i·(√3/2)·(b−c).
    // ⚠️ Здесь легко потерять знак дважды и получить X[1] и X[2] местами —
    // спектр останется правдоподобным, NaN не будет, поймает только сверка с
    // прямым ДПФ (первая редакция этого кодлета ровно так и падала).
    var C3 = -0.5;                                  // cos(2π/3)
    var S3 = 0.8660254037844386;                    // sin(2π/3) = √3/2
    function dft3(ar, ai, br, bi, cr, ci, out) {
        var t1r = br + cr, t1i = bi + ci;
        var t2r = br - cr, t2i = bi - ci;
        out[0] = ar + t1r;             out[1] = ai + t1i;
        var mr = ar + C3 * t1r, mi = ai + C3 * t1i;
        var nr = S3 * t2i, ni = -S3 * t2r;          // −i·(√3/2)·(b−c)
        out[2] = mr + nr;              out[3] = mi + ni;
        out[4] = mr - nr;              out[5] = mi - ni;
    }

    // ⚠️ Таблицы — НА УРОВНЕ МОДУЛЯ. Первая редакция строила `[1, C5_1, …]`
    // литералом внутри dft5 и звала Math.cos/Math.sin внутри dft15: на длине
    // 7680 это 262 тысячи выделений и миллион тригонометрических вызовов на
    // канал за чанк, и выигрыш от смены алгоритма выходил РОВНО НУЛЕВОЙ
    // (×1.00 против Блюстейна при ×5.5 на соседней длине без этой ветки).
    var C5_1 = 0.30901699437494745, S5_1 = -0.9510565162951535;   // cos/sin(2π/5)
    var C5_2 = -0.8090169943749473, S5_2 = -0.5877852522924732;   // cos/sin(4π/5)
    var W5_C = new Float64Array([1, C5_1, C5_2, C5_2, C5_1]);
    var W5_S = new Float64Array([0, S5_1, S5_2, -S5_2, -S5_1]);
    function dft5(re5, im5, out) {
        // Прямая свёртка 5×5 по таблице — 5 точек, разворачивать смысла нет.
        for (var k = 0; k < 5; k++) {
            var sr = 0, si = 0;
            for (var m = 0; m < 5; m++) {
                var idx = (k * m) % 5;
                var c = W5_C[idx], s = W5_S[idx];
                sr += re5[m] * c - im5[m] * s;
                si += re5[m] * s + im5[m] * c;
            }
            out[2 * k] = sr; out[2 * k + 1] = si;
        }
    }

    // Повороты внутреннего шага ДПФ-15: W_15^{b·k1}, b<5, k1<3.
    var W15_C = new Float64Array(15), W15_S = new Float64Array(15);
    for (var _k1 = 0; _k1 < 3; _k1++) {
        for (var _b = 0; _b < 5; _b++) {
            var _a = -2 * Math.PI * _b * _k1 / 15;
            W15_C[_b + 5 * _k1] = Math.cos(_a);
            W15_S[_b + 5 * _k1] = Math.sin(_a);
        }
    }
    // Полная матрица ДПФ-15 — альтернативная реализация того же шага.
    var W15M_C = new Float64Array(225), W15M_S = new Float64Array(225);
    for (var _k = 0; _k < 15; _k++) {
        for (var _m = 0; _m < 15; _m++) {
            var _ang = -2 * Math.PI * ((_k * _m) % 15) / 15;
            W15M_C[_k * 15 + _m] = Math.cos(_ang);
            W15M_S[_k * 15 + _m] = Math.sin(_ang);
        }
    }

    var _t3 = new Float64Array(6);
    var _r5 = new Float64Array(5), _i5 = new Float64Array(5), _o5 = new Float64Array(10);
    var _r15 = new Float64Array(15), _i15 = new Float64Array(15);
    var _o15r = new Float64Array(15), _o15i = new Float64Array(15);

    /**
     * ДПФ длины n2 ∈ {1,3,5,15} над r элементами массива с шагом `stride`,
     * начиная с `off`. Результат кладётся туда же (набор индексов тот же).
     */
    function dftSmall(re, im, off, stride, n2) {
        if (n2 === 1) { return; }
        if (n2 === 3) {
            var i0 = off, i1 = off + stride, i2 = off + 2 * stride;
            dft3(re[i0], im[i0], re[i1], im[i1], re[i2], im[i2], _t3);
            re[i0] = _t3[0]; im[i0] = _t3[1];
            re[i1] = _t3[2]; im[i1] = _t3[3];
            re[i2] = _t3[4]; im[i2] = _t3[5];
            return;
        }
        if (n2 === 5) {
            for (var k = 0; k < 5; k++) { var p = off + k * stride; _r5[k] = re[p]; _i5[k] = im[p]; }
            dft5(_r5, _i5, _o5);
            for (var k2 = 0; k2 < 5; k2++) { var q = off + k2 * stride; re[q] = _o5[2 * k2]; im[q] = _o5[2 * k2 + 1]; }
            return;
        }
        if (n2 === 15) { dft15(re, im, off, stride); return; }
    }

    /** ДПФ-15 разложением 15 = 3·5 (кодлеты + таблица поворотов). */
    function dft15(re, im, off, stride) {
        for (var g = 0; g < 15; g++) { var s = off + g * stride; _r15[g] = re[s]; _i15[g] = im[s]; }
        // шаг 1: 5 ДПФ-3 по прореженным с шагом 5
        for (var b = 0; b < 5; b++) {
            dft3(_r15[b], _i15[b], _r15[b + 5], _i15[b + 5], _r15[b + 10], _i15[b + 10], _t3);
            _r15[b] = _t3[0]; _i15[b] = _t3[1];
            _r15[b + 5] = _t3[2]; _i15[b + 5] = _t3[3];
            _r15[b + 10] = _t3[4]; _i15[b + 10] = _t3[5];
        }
        // шаг 2: поворот W_15^{b·k1} по таблице
        for (var k1 = 1; k1 < 3; k1++) {
            for (var b2 = 1; b2 < 5; b2++) {
                var idx = b2 + 5 * k1;
                var c = W15_C[idx], sI = W15_S[idx];
                var xr = _r15[idx], xi = _i15[idx];
                _r15[idx] = xr * c - xi * sI;
                _i15[idx] = xr * sI + xi * c;
            }
        }
        // шаг 3: 3 ДПФ-5, выход в позицию k1 + 3·k2
        for (var k1b = 0; k1b < 3; k1b++) {
            for (var m = 0; m < 5; m++) { _r5[m] = _r15[m + 5 * k1b]; _i5[m] = _i15[m + 5 * k1b]; }
            dft5(_r5, _i5, _o5);
            for (var k2 = 0; k2 < 5; k2++) {
                var dst = off + (k1b + 3 * k2) * stride;
                re[dst] = _o5[2 * k2]; im[dst] = _o5[2 * k2 + 1];
            }
        }
    }

    /** ДПФ-15 матрицей 15×15 — вторая реализация того же, для сравнения замером. */
    function dft15Matrix(re, im, off, stride) {
        for (var g = 0; g < 15; g++) { var s = off + g * stride; _r15[g] = re[s]; _i15[g] = im[s]; }
        for (var k = 0; k < 15; k++) {
            var sr = 0, si = 0, row = k * 15;
            for (var m = 0; m < 15; m++) {
                var c = W15M_C[row + m], sn = W15M_S[row + m];
                var xr = _r15[m], xi = _i15[m];
                sr += xr * c - xi * sn;
                si += xr * sn + xi * c;
            }
            _o15r[k] = sr; _o15i[k] = si;
        }
        for (var k2 = 0; k2 < 15; k2++) { var d = off + k2 * stride; re[d] = _o15r[k2]; im[d] = _o15i[k2]; }
    }

    /**
     * Комплексное БПФ на месте, длина plan.n.
     *
     * Вывод (N = N1·N2, n = n1·N2 + n2, k = k1 + N1·k2):
     *   X[k] = Σ_n x[n]·W_N^{nk}
     *        = Σ_{n2} W_N^{n2·k} · Σ_{n1} x[n1·N2+n2]·W_{N1}^{n1·k1}
     *        = Σ_{n2} ( A_{n2}[k1] · W_N^{n2·k1} ) · W_{N2}^{n2·k2}
     * то есть: N2 преобразований длиной N1 по прореженным с шагом N2 →
     * поворот → N1 преобразований длиной N2, и бин k1+N1·k2 лежит по индексу
     * k1 + N1·k2 сам собой.
     */
    function fft(p, re, im) {
        var N1 = p.n1, N2 = p.n2, n = p.n;
        if (N2 === 1) { fft2(re, im, p.tw2, 0, n); return; }

        var sr = p.scratchRe, si = p.scratchIm;
        var cr = p.colRe, ci = p.colIm;
        var tc = p.twStep.cos, ts = p.twStep.sin;

        for (var b = 0; b < N2; b++) {
            for (var i = 0; i < N1; i++) { cr[i] = re[i * N2 + b]; ci[i] = im[i * N2 + b]; }
            fft2(cr, ci, p.tw2, 0, N1);
            var to = b * N1;
            for (var k1 = 0; k1 < N1; k1++) {
                var xr = cr[k1], xi = ci[k1];
                var c = tc[to + k1], s = ts[to + k1];
                // раскладка «по k1, потом по b» — шаг 3 читает столбцами
                sr[k1 * N2 + b] = xr * c - xi * s;
                si[k1 * N2 + b] = xr * s + xi * c;
            }
        }
        for (var k1b = 0; k1b < N1; k1b++) { dftSmall(sr, si, k1b * N2, 1, N2); }
        // sr[k1*N2 + k2] → выход X[k1 + N1*k2]
        for (var k1c = 0; k1c < N1; k1c++) {
            var base = k1c * N2;
            for (var k2 = 0; k2 < N2; k2++) {
                re[k1c + N1 * k2] = sr[base + k2];
                im[k1c + N1 * k2] = si[base + k2];
            }
        }
    }

    /** Обратное БПФ на месте (с делением на n). */
    function ifft(p, re, im) {
        var n = p.n;
        for (var i = 0; i < n; i++) { im[i] = -im[i]; }
        fft(p, re, im);
        var inv = 1 / n;
        for (var j = 0; j < n; j++) { re[j] *= inv; im[j] = -im[j] * inv; }
    }

    // ── Пара вещественных сигналов за одно комплексное БПФ ───────────────
    // Спектр вещественного сигнала эрмитов, то есть половина работы обычного
    // комплексного БПФ на нём пропадает. Стерео даёт ровно два вещественных
    // сигнала: кладём левый в вещественную часть, правый в мнимую, считаем ОДНО
    // преобразование и разбираем результат. Для STFT/ISTFT стерео это ×2 к
    // числу преобразований — независимо от того, каким алгоритмом они считаются.
    //   Z = x + i·y  ⇒  X[k] = (Z[k] + conj(Z[n−k]))/2,  Y[k] = (Z[k] − conj(Z[n−k]))/(2i)

    /** После fft() по z=x+i·y разложить бины [0,bins) в два спектра. */
    function untangleTwoReal(re, im, n, bins, xr, xi, yr, yi) {
        xr[0] = re[0]; xi[0] = 0;
        yr[0] = im[0]; yi[0] = 0;
        for (var k = 1; k < bins; k++) {
            var m = n - k;
            var ar = re[k], ai = im[k], br = re[m], bi = im[m];
            xr[k] = 0.5 * (ar + br);
            xi[k] = 0.5 * (ai - bi);
            yr[k] = 0.5 * (ai + bi);
            yi[k] = 0.5 * (br - ar);
        }
    }

    /**
     * Собрать z = X + i·Y из двух спектров, заданных бинами [0,bins), достроив
     * эрмитову симметрию. re/im обнуляются целиком — вызывающему не нужно.
     *
     * ⚠️⚠️ Мнимые части бина 0 (и Найквиста) ИГНОРИРУЮТСЯ, и это не небрежность.
     * У вещественного сигнала они равны нулю по построению, но на вход сюда
     * приходит ВЫХОД МОДЕЛИ, а он про эрмитовость ничего не знает и кладёт в
     * нулевой бин ненулевую мнимую часть. Раздельная обработка её просто
     * теряла: постоянная добавка уходила целиком в мнимую часть результата, а
     * оттуда бралась только вещественная. При упаковке двух каналов в одно
     * преобразование такая добавка утекла бы В СОСЕДНИЙ КАНАЛ — постоянной
     * составляющей величиной im[0]/n на каждом кадре, то есть слышимым
     * подгуживанием. Обнуление здесь делает упакованный путь ПОБИТОВО тем же,
     * что раздельный; сторож на это есть.
     */
    function tangleTwoReal(re, im, n, bins, xr, xi, yr, yi) {
        re.fill(0); im.fill(0);
        var half = n >> 1;
        var top = bins < half ? bins : half;
        re[0] = xr[0];
        im[0] = yr[0];
        for (var k = 1; k < top; k++) {
            // Z[k] = X[k] + i·Y[k]
            re[k] = xr[k] - yi[k];
            im[k] = xi[k] + yr[k];
            // Z[n−k] = conj(X[k]) + i·conj(Y[k])
            re[n - k] = xr[k] + yi[k];
            im[n - k] = -xi[k] + yr[k];
        }
        // Nyquist, when the caller hands it in (bins = n/2 + 1 — a model over the
        // whole one-sided spectrum, melband-shared.js): real for both signals, its
        // imaginary part dropped exactly as bin 0's above and as irfft drops it.
        // Until 28.10.2026 the bin was lost here; the MDX engines never pass it
        // (dim_f < n/2), so their output is unchanged byte for byte.
        if (bins > half) { re[half] = xr[half]; im[half] = yr[half]; }
    }

    return {
        plan: plan, fft: fft, ifft: ifft, fft2: fft2, radix2Twiddles: radix2Twiddles,
        untangleTwoReal: untangleTwoReal, tangleTwoReal: tangleTwoReal,
        // открыты для сторожа: обе реализации ДПФ-15 обязаны совпадать
        _dft15: dft15, _dft15Matrix: dft15Matrix,
    };
});
